const MICROSOFT_AUTHORITY = "https://login.microsoftonline.com/common/oauth2/v2.0";
const MICROSOFT_GRAPH_ROOT = "https://graph.microsoft.com/v1.0";
const MICROSOFT_CALENDAR_SCOPE = "openid profile User.Read Calendars.ReadWrite";
export const MICROSOFT_MAIL_SCOPE = "openid profile User.Read Mail.ReadWrite Mail.Send Calendars.ReadWrite";
export const MICROSOFT_CALENDAR_CALLBACK_PATH = "/microsoft-calendar-auth.html";
export const CLS_CONNECT_MICROSOFT_CLIENT_ID =
  import.meta.env?.VITE_MICROSOFT_CALENDAR_CLIENT_ID ||
  "4516c74c-8387-4af1-aec9-973c12ca7a21";

function randomString(length = 64) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const cryptoObj = typeof crypto !== "undefined" ? crypto : (typeof window !== "undefined" ? window.crypto : null);
  let values;
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    values = cryptoObj.getRandomValues(new Uint8Array(length));
  } else {
    values = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
      values[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(values, (value) => chars[value % chars.length]).join("");
}

function base64UrlEncode(bytes) {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

async function createCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder();
  const cryptoObj = typeof crypto !== "undefined" ? crypto : (typeof window !== "undefined" ? window.crypto : null);
  if (!cryptoObj || !cryptoObj.subtle) {
    // Fallback if subtle crypto is not available (insecure context)
    // Basic SHA-256 fallback representation if not available (since this is only client calendar Oauth, we can throw clear error or try fallback)
    throw new Error("Secure cryptography (subtle) is required for Microsoft Calendar OAuth but not available in this context.");
  }
  const digest = await cryptoObj.subtle.digest("SHA-256", encoder.encode(codeVerifier));
  return base64UrlEncode(new Uint8Array(digest));
}

export function buildMicrosoftRedirectUri(origin = window.location.origin) {
  return `${origin.replace(/\/$/, "")}${MICROSOFT_CALENDAR_CALLBACK_PATH}`;
}

export async function microsoftGraphFetch(accessToken, url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(options.headers || {}),
    },
  });

  if (!response.ok) {
    const text = await response.text();
    if (text.includes("AADSTS500014") && text.includes("outlook.office365.com")) {
      throw new Error(
        "This Microsoft account does not have an active Exchange Online calendar. Choose another Microsoft account or ask your Microsoft 365 administrator to restore Exchange Online."
      );
    }

    throw new Error(`Microsoft Graph request failed (${response.status}): ${text || response.statusText}`);
  }

  return response.status === 204 ? null : response.json();
}

function extractAuthCodeFromPopup(popup, expectedState) {
  try {
    if (!popup || popup.closed) {
      throw new Error("Microsoft sign-in was closed before it finished.");
    }

    const href = popup.location.href;
    if (!href || popup.location.origin !== window.location.origin) {
      return null;
    }

    const url = new URL(href);
    const error = url.searchParams.get("error");
    if (error) {
      throw new Error(url.searchParams.get("error_description") || error);
    }

    const code = url.searchParams.get("code");
    if (!code) {
      return null;
    }
    if (url.searchParams.get("state") !== expectedState) {
      throw new Error("Microsoft sign-in returned an invalid security state. Try again.");
    }

    popup.close();
    return code;
  } catch (error) {
    if (error instanceof DOMException) {
      return null;
    }
    throw error;
  }
}

async function exchangeMicrosoftCodeForToken(clientId, code, codeVerifier, scope = MICROSOFT_CALENDAR_SCOPE) {
  const params = new URLSearchParams({
    client_id: clientId,
    scope,
    code,
    redirect_uri: buildMicrosoftRedirectUri(),
    grant_type: "authorization_code",
    code_verifier: codeVerifier,
  });

  const response = await fetch(`${MICROSOFT_AUTHORITY}/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: params.toString(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Microsoft token request failed (${response.status}): ${text || response.statusText}`);
  }

  return response.json();
}

export async function requestMicrosoftAccessToken({
  clientId = CLS_CONNECT_MICROSOFT_CLIENT_ID,
  scope = MICROSOFT_CALENDAR_SCOPE,
  popupName = "aidPlannerMicrosoftCalendar",
} = {}) {
  if (!clientId) {
    throw new Error("Enter a Microsoft OAuth client ID first.");
  }

  const codeVerifier = randomString(96);
  const codeChallenge = await createCodeChallenge(codeVerifier);
  const state = randomString(32);
  const redirectUri = buildMicrosoftRedirectUri();
  const authUrl = new URL(`${MICROSOFT_AUTHORITY}/authorize`);
  authUrl.searchParams.set("client_id", clientId);
  authUrl.searchParams.set("response_type", "code");
  authUrl.searchParams.set("redirect_uri", redirectUri);
  authUrl.searchParams.set("response_mode", "query");
  authUrl.searchParams.set("scope", scope);
  authUrl.searchParams.set("code_challenge", codeChallenge);
  authUrl.searchParams.set("prompt", "select_account");
  authUrl.searchParams.set("code_challenge_method", "S256");
  authUrl.searchParams.set("state", state);

  const popup = window.open(authUrl.toString(), popupName, "width=520,height=720");
  if (!popup) {
    throw new Error("Microsoft sign-in popup was blocked. Allow popups and try again.");
  }

  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    let isExchanging = false;
    const timer = window.setInterval(async () => {
      try {
        if (isExchanging) return;
        const code = extractAuthCodeFromPopup(popup, state);
        if (!code) {
          if (Date.now() - startedAt > 120000) {
            window.clearInterval(timer);
            try {
              popup.close();
            } catch {}
            reject(new Error("Microsoft sign-in took too long. Try again."));
          }
          return;
        }

        isExchanging = true;
        window.clearInterval(timer);
        const tokenPayload = await exchangeMicrosoftCodeForToken(clientId, code, codeVerifier, scope);
        if (!tokenPayload.access_token) {
          throw new Error("Microsoft sign-in completed without returning an access token.");
        }
        resolve(tokenPayload.access_token);
      } catch (error) {
        window.clearInterval(timer);
        try {
          popup.close();
        } catch {}
        reject(error instanceof Error ? error : new Error("Could not complete Microsoft sign-in."));
      }
    }, 500);
  });
}

export async function requestMicrosoftCalendarAccessToken(
  clientId = CLS_CONNECT_MICROSOFT_CLIENT_ID
) {
  return requestMicrosoftAccessToken({ clientId });
}

export async function listOutlookCalendars(accessToken) {
  const payload = await microsoftGraphFetch(
    accessToken,
    `${MICROSOFT_GRAPH_ROOT}/me/calendars?$select=id,name,canEdit,isDefaultCalendar`
  );

  return (payload.value || []).map((item) => ({
    id: item.id,
    summary: item.name || item.id,
    canEdit: Boolean(item.canEdit),
    primary: Boolean(item.isDefaultCalendar),
  }));
}

export async function listOutlookCalendarEvents(accessToken, calendarId, timeMin, timeMax) {
  if (!calendarId) {
    throw new Error("Choose an Outlook calendar first.");
  }

  const url = new URL(`${MICROSOFT_GRAPH_ROOT}/me/calendars/${encodeURIComponent(calendarId)}/calendarView`);
  if (timeMin) url.searchParams.set("startDateTime", `${timeMin}T00:00:00.000Z`);
  if (timeMax) url.searchParams.set("endDateTime", `${timeMax}T23:59:59.999Z`);
  url.searchParams.set("$orderby", "start/dateTime");
  url.searchParams.set("$select", "id,iCalUId,subject,bodyPreview,body,start,end,location,organizer,isCancelled,sensitivity,lastModifiedDateTime");

  const payload = await microsoftGraphFetch(accessToken, url.toString(), {
    headers: {
      Prefer: 'outlook.timezone="Australia/Sydney", outlook.body-content-type="text"',
    },
  });
  return payload.value || [];
}

export async function createOutlookCalendarEvent(accessToken, calendarId, event) {
  if (!calendarId) {
    throw new Error("Choose an Outlook calendar first.");
  }

  return microsoftGraphFetch(accessToken, `${MICROSOFT_GRAPH_ROOT}/me/calendars/${encodeURIComponent(calendarId)}/events`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(event),
  });
}

export async function updateOutlookCalendarEvent(accessToken, calendarId, eventId, event) {
  if (!calendarId || !eventId) throw new Error("Choose an Outlook calendar and linked event first.");
  return microsoftGraphFetch(accessToken, `${MICROSOFT_GRAPH_ROOT}/me/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(event),
  });
}

export async function deleteOutlookCalendarEvent(accessToken, calendarId, eventId) {
  if (!calendarId || !eventId) throw new Error("Choose an Outlook calendar and appointment first.");
  return microsoftGraphFetch(accessToken, `${MICROSOFT_GRAPH_ROOT}/me/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`, {
    method: "DELETE",
  });
}
